/**
 * API service for conversation management.
 * Provides functions to list, create, and manage AI chat conversations.
 */

import { api, getCSRFToken } from './api'

/**
 * Summary information for a conversation in list views.
 */
export interface ConversationSummary {
  id: number
  title: string
  message_count: number
  is_active: boolean
  has_summary: boolean
  created_at: string
  updated_at: string
}

/**
 * Full conversation details including messages.
 */
export interface ConversationDetail {
  id: number
  title: string
  summary: string
  message_count: number
  total_tokens: number
  is_active: boolean
  created_at: string
  updated_at: string
  messages: ConversationMessage[]
}

/**
 * Individual message in a conversation.
 */
export interface ConversationMessage {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  token_count: number
  is_summarized: boolean
  created_at: string
}

/**
 * Response from the conversations list endpoint.
 */
interface ConversationsListResponse {
  conversations: ConversationSummary[]
}

/**
 * Response from the messages endpoint.
 */
interface MessagesResponse {
  conversation_id: number
  messages: ConversationMessage[]
  count: number
}

/**
 * Fetches the list of conversations for the current user.
 *
 * @param limit - Maximum number of conversations to return
 * @param includeInactive - Whether to include archived conversations
 * @returns Promise<ConversationSummary[]> List of conversations
 */
export const fetchConversations = async (
  limit: number = 20,
  includeInactive: boolean = false
): Promise<ConversationSummary[]> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      include_inactive: includeInactive.toString(),
    })

    const response = await api.get<ConversationsListResponse>(
      `/api/conversations/?${params}`
    )

    return response.data.conversations
  } catch (error) {
    console.error('Error fetching conversations:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      context: { error },
    })
    throw error
  }
}

/**
 * Creates a new conversation.
 *
 * @param title - Optional title for the conversation
 * @returns Promise<ConversationSummary> The created conversation
 */
export const createConversation = async (
  title: string = ''
): Promise<ConversationSummary> => {
  try {
    const csrfToken = await getCSRFToken()

    const response = await api.post<ConversationSummary>(
      '/api/conversations/',
      { title },
      {
        headers: {
          'X-CSRFToken': csrfToken,
        },
      }
    )

    return response.data
  } catch (error) {
    console.error('Error creating conversation:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      context: { error },
    })
    throw error
  }
}

/**
 * Fetches a specific conversation with its messages.
 *
 * @param conversationId - The ID of the conversation
 * @param messageLimit - Maximum number of messages to return
 * @returns Promise<ConversationDetail> The conversation with messages
 */
export const fetchConversation = async (
  conversationId: number,
  messageLimit: number = 50
): Promise<ConversationDetail> => {
  try {
    const params = new URLSearchParams({
      message_limit: messageLimit.toString(),
    })

    const response = await api.get<ConversationDetail>(
      `/api/conversations/${conversationId}/?${params}`
    )

    return response.data
  } catch (error) {
    console.error('Error fetching conversation:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      context: { conversationId, error },
    })
    throw error
  }
}

/**
 * Archives (soft deletes) a conversation.
 *
 * @param conversationId - The ID of the conversation to archive
 * @returns Promise<void>
 */
export const archiveConversation = async (
  conversationId: number
): Promise<void> => {
  try {
    const csrfToken = await getCSRFToken()

    await api.delete(`/api/conversations/${conversationId}/`, {
      headers: {
        'X-CSRFToken': csrfToken,
      },
    })
  } catch (error) {
    console.error('Error archiving conversation:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      context: { conversationId, error },
    })
    throw error
  }
}

/**
 * Fetches messages for a specific conversation.
 *
 * @param conversationId - The ID of the conversation
 * @param limit - Maximum number of messages to return
 * @param includeSummarized - Whether to include summarized messages
 * @returns Promise<ConversationMessage[]> List of messages
 */
export const fetchConversationMessages = async (
  conversationId: number,
  limit: number = 50,
  includeSummarized: boolean = true
): Promise<ConversationMessage[]> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      include_summarized: includeSummarized.toString(),
    })

    const response = await api.get<MessagesResponse>(
      `/api/conversations/${conversationId}/messages/?${params}`
    )

    return response.data.messages
  } catch (error) {
    console.error('Error fetching conversation messages:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      context: { conversationId, error },
    })
    throw error
  }
}
